import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { find } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCoverageType } from '../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { ProviderType } from '../../models/ProviderDetails';
import {
  getShowEditPCP,
  isCnsRestrictedEditPcp,
} from '../../utils/primaryCare.utils';
import {
  getCurrentMember,
  getPolicyNumber,
  getReportingCode,
} from '../../utils/user.utils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { useShallow } from 'zustand/react/shallow';
import { EditPCPMenu } from '../PrimaryCareProvider/EditPCPMenu/EditPCPMenu';
import { getFormattedDate } from '../Utils/datesUtils/formatDate';
import {
  LayoutContainer,
  YourPCPContainer,
  YourPCPMessageDetailsContainer,
} from './DetailsPageHeader.styled';

export type Props = {
  providerPCPId: string;
  endDate: string;
  displayFutureTerminationDate?: boolean;
  locationTotalCount?: number;
  providerType: string;
  isCurrentPCP: boolean;
  memberPcpName: boolean;
  hidePCPContent: boolean;
};

export const ProviderDetailBanner = ({
  memberPcpName,
  providerPCPId,
  endDate,
  displayFutureTerminationDate,
  locationTotalCount,
  isCurrentPCP,
  providerType,
  hidePCPContent,
}: Props) => {
  const { t } = useTranslation();
  const currentMember = getCurrentMember();
  const lob = currentMember?.lineOfBusiness;
  const isIfpMember = currentMember?.isIFPMember || false;
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  const kelseySeyboldClinicName = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.KELSEY_SEYBOLD_CLINIC_NAME,
  });
  const isPreEffective = useCurrentMemberIsPreEffective();

  const { data: pcpData } = usePrimaryCare();
  const { primaryCare } = pcpData;
  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;
  const { isKelseySeyboldPlan } = currentMember;

  const isPcpKelseySeyboldClinic =
    memberPcpName === kelseySeyboldClinicName?.message;
  const coverageType = useCoverageType();
  const customMesssges = useLagoon('custom-messages')();
  const memberPolicyNumber = getPolicyNumber(currentMember, coverageType);
  const memberReportingCode = getReportingCode(currentMember, coverageType);
  const isCnsReportingCode = isCnsRestrictedEditPcp(
    customMesssges,
    memberReportingCode,
    memberPolicyNumber
  );
  const isKelseySeyboldClinic =
    isIfpMember && isKelseySeyboldPlan && isPcpKelseySeyboldClinic;

  const [enableSuppressChoosePcpForCurrentPcp] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUPPRESS_CHOOSE_PCP_FOR_CURRENT_PCP,
  ]);
  const { choosePCP } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const showEditPCP =
    getShowEditPCP(currentMember.editPcp, lockInIndicator) &&
    !isKelseySeyboldClinic &&
    !isCnsReportingCode &&
    !(enableSuppressChoosePcpForCurrentPcp && choosePCP);

  const YourPCPMessage = () => {
    if (
      hidePCPContent ||
      !isCurrentPCP ||
      getFormattedDate(endDate) ||
      displayFutureTerminationDate
    ) {
      return null;
    }

    return (
      <React.Fragment>
        <YourPCPContainer css={!showEditPCP ? { width: '250px' } : {}}>
          <LayoutContainer css={{ alignItems: 'flex-start' }}>
            <YourPCPMessageDetailsContainer>
              <IconSymbol
                color="$core.color.brand.100"
                icon="check"
                size="$sm"
              />
              <Text
                css={{
                  'abyss-text-root': {
                    fontSize: '12.64px',
                    fontWeight: '700',
                    fontStyle: 'normal',
                    lineHeight: '16px',
                    color: '$core.color.brand.100 ! important',
                  },
                }}
                data-testid="pcp-banner-content"
              >
                {t('PCP_BANNER.CONTENT')}
              </Text>
            </YourPCPMessageDetailsContainer>
            {showEditPCP ? (
              <EditPCPMenu
                badge
                dependentSeqNbr={currentMember?.demographics?.dependentSeqNbr}
                isPreEffective={isPreEffective}
                lineOfBusiness={lob}
                locationForAnalytics="Edit PCP"
                providerID={providerPCPId}
                providerType={
                  providerType === ProviderType.PROVIDER_GROUP
                    ? ProviderType.MEDICAL_GROUP
                    : providerType
                }
                showPcpLocation={locationTotalCount}
              />
            ) : null}
          </LayoutContainer>
        </YourPCPContainer>
      </React.Fragment>
    );
  };
  return (
    <React.Fragment>
      <YourPCPMessage />
    </React.Fragment>
  );
};
